// src/admin/PrintTuitionList.js
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useOutletContext } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import { firestore } from '../firebaseConfig';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { subscribeTuitionFeesByClass } from '../FirebaseService';
import classRoomsData from '../data/classRooms.json';
import './PrintTuitionList.css';

const PrintTuitionList = () => {
  const {
    currentYearId,
    studentsData,
    classMap,
    catechistsData
  } = useContext(StudentContext);

  const outletContext = useOutletContext();
  const sidebarSelectedClass = outletContext?.selectedClassForOutlet || 'all';

  const [activeClassId, setActiveClassId] = useState('');
  const [currentYearName, setCurrentYearName] = useState('');
  const [tuitionMap, setTuitionMap] = useState({});

  // 🌟 TÙY CHỌN KHỔ GIẤY IN: 'portrait' (Dọc) | 'landscape' (Nằm ngang)
  const [paperOrientation, setPaperOrientation] = useState('portrait');

  // Đồng bộ với lớp đang chọn ở Sidebar
  useEffect(() => {
    if (sidebarSelectedClass && sidebarSelectedClass !== 'all') {
      setActiveClassId(sidebarSelectedClass);
    } else if (!activeClassId) {
      const firstClassKey = Object.keys(classMap)[0];
      if (firstClassKey) setActiveClassId(firstClassKey);
    }
  }, [sidebarSelectedClass, classMap, activeClassId]);

  // Lấy tên niên khóa hiện tại
  useEffect(() => {
    const fetchCurrentYearName = async () => {
      try {
        const qYears = query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc'));
        const yearsSnap = await getDocs(qYears);
        const curYear = yearsSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .find(y => y.id === currentYearId || y.isCurrent);
        if (curYear) {
          setCurrentYearName(curYear.name || curYear.id);
        }
      } catch (err) {
        console.error('Lỗi khi tải thông tin niên khóa:', err);
      }
    };
    fetchCurrentYearName();
  }, [currentYearId]);

  // Lắng nghe Realtime dữ liệu đóng học phí của lớp đang chọn
  useEffect(() => {
    if (!currentYearId || !activeClassId) return;
    const unsubscribe = subscribeTuitionFeesByClass(currentYearId, activeClassId, (data) => {
      setTuitionMap(data || {});
    });
    return () => unsubscribe();
  }, [currentYearId, activeClassId]);

  const currentClassName = classMap[activeClassId] || activeClassId || 'Chưa chọn lớp';

  // Thông tin phòng học và ngành
  const roomInfo = useMemo(() => {
    if (!activeClassId) return null;
    return classRoomsData.find(
      r => r.classId === activeClassId ||
           (r.className && r.className.trim().toLowerCase() === currentClassName.trim().toLowerCase())
    );
  }, [activeClassId, currentClassName]);

  const roomName = roomInfo?.room || 'Chưa xếp phòng';
  const groupName = roomInfo?.block || 'Chung';

  // 🌟 XÁC ĐỊNH MÀU SẮC BANNER THEO TỪNG NGÀNH
  const groupColorClass = useMemo(() => {
    const norm = (groupName || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    if (norm.includes('chien')) return 'banner-chien-con';
    if (norm.includes('au')) return 'banner-au';
    if (norm.includes('thieu')) return 'banner-thieu';
    if (norm.includes('nghia')) return 'banner-nghia';
    if (norm.includes('hiep')) return 'banner-hiep-si';
    return 'banner-default';
  }, [groupName]);

  // Danh sách Giáo lý viên đứng lớp
  const assignedTeachers = useMemo(() => {
    if (!currentClassName || !catechistsData) return [];

    const matched = catechistsData.filter(c => {
      if ((c.status || 'active') === 'inactive') return false;
      return c.className === currentClassName || c.className === activeClassId;
    });

    matched.sort((a, b) => {
      const aIsTuSi = (a.religious || 'Giáo lý viên') === 'Tu Sĩ' ? 0 : 1;
      const bIsTuSi = (b.religious || 'Giáo lý viên') === 'Tu Sĩ' ? 0 : 1;
      return aIsTuSi - bIsTuSi;
    });

    return matched.map(t => {
      const isTuSi = (t.religious || 'Giáo lý viên') === 'Tu Sĩ';
      const isFemale = (t.gender || 'Nam') === 'Nữ';

      let prefix = isTuSi ? (isFemale ? 'Sr' : 'Thầy') : (isFemale ? 'Chị' : 'Anh');
      const holy = t.holyName ? `${t.holyName} ` : '';
      return `${prefix} ${holy}${t.fullName || ''}`.trim();
    });
  }, [currentClassName, activeClassId, catechistsData]);

  // Sắp xếp danh sách thiếu nhi theo Tên A-Z (Ẩn thiếu nhi Nghỉ luôn)
  const sortedStudents = useMemo(() => {
    const rawList = studentsData[activeClassId] || [];

    const list = rawList.filter((student) => {
      const status = (student.status || '').trim().toLowerCase();
      return status !== 'nghỉ luôn';
    });

    const getLastName = (fullName) => {
      if (!fullName) return '';
      const parts = fullName.trim().split(/\s+/);
      return parts[parts.length - 1].toLowerCase();
    };

    list.sort((a, b) => {
      const lastNameA = getLastName(a.fullName);
      const lastNameB = getLastName(b.fullName);
      const cmp = lastNameA.localeCompare(lastNameB, 'vi');
      if (cmp !== 0) return cmp;
      return (a.fullName || '').localeCompare(b.fullName || '', 'vi');
    });

    return list;
  }, [studentsData, activeClassId]);

  // Format ngày YYYY-MM-DD sang DD/MM/YYYY
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className={`ptl-container ${paperOrientation === 'landscape' ? 'ptl-landscape-mode' : ''}`}>
      {/* 🌟 STYLE NẠP ĐỘNG ĐỂ TRÌNH DUYỆT IN ĐÚNG KHỔ GIẤY A4 ĐỨNG HOẶC NẰM NGANG */}
      <style>
        {`
          @media print {
            @page {
              size: A4 ${paperOrientation};
              margin: ${paperOrientation === 'landscape' ? '12mm 15mm' : '15mm 12mm'};
            }
          }
        `}
      </style>

      {/* THANH CÔNG CỤ ĐIỀU KHIỂN (ẨN KHI IN) */}
      <div className="ptl-toolbar no-print">
        <div className="ptl-toolbar-left">
          <label className="ptl-toolbar-label">Lớp:</label>
          <select
            className="ptl-select-class"
            value={activeClassId}
            onChange={(e) => setActiveClassId(e.target.value)}
          >
            {Object.entries(classMap).map(([cId, cName]) => (
              <option key={cId} value={cId}>{cName}</option>
            ))}
          </select>

          {/* CHỌN KHỔ GIẤY IN */}
          <div className="ptl-orientation-switch">
            <label className="ptl-toolbar-label">Khổ in:</label>
            <button
              type="button"
              className={`ptl-orientation-btn ${paperOrientation === 'portrait' ? 'active' : ''}`}
              onClick={() => setPaperOrientation('portrait')}
            >
              📄 A4 Dọc
            </button>
            <button
              type="button"
              className={`ptl-orientation-btn ${paperOrientation === 'landscape' ? 'active' : ''}`}
              onClick={() => setPaperOrientation('landscape')}
            >
              📑 A4 Nằm ngang
            </button>
          </div>
        </div>

        <button onClick={handlePrint} className="ptl-btn-print">
          🖨️ In Danh Sách ({paperOrientation === 'landscape' ? 'A4 Ngang' : 'A4 Dọc'})
        </button>
      </div>

      {/* TỜ GIẤY IN CHUẨN A4 */}
      <div className={`ptl-sheet ${paperOrientation === 'landscape' ? 'ptl-sheet-landscape' : ''}`}>
        {/* PHẦN ĐẦU: XỨ ĐOÀN */}
        <div className="ptl-header-top">
          <div className="ptl-origin-left">
            <div className="ptl-parish-name">GIÁO XỨ AN NHƠN</div>
            <div className="ptl-group-name">XỨ ĐOÀN PHAOLÔ LÊ VĂN LỘC</div>
            <div className="ptl-header-divider"></div>
          </div>
        </div>

        {/* TIÊU ĐỀ: BANNER MÀU THEO NGÀNH */}
        <section className="ptl-title-section">
          <div className={`ptl-title-banner ${groupColorClass}`}>
            <h1 className="ptl-main-title">
              DANH SÁCH ĐÓNG HỌC PHÍ {groupName.toUpperCase().startsWith('NGÀNH') ? groupName.toUpperCase() : `NGÀNH ${groupName.toUpperCase()}`}
            </h1>
            <h2 className="ptl-class-title">LỚP {currentClassName.toUpperCase()}</h2>
          </div>
          <p className="ptl-year-text">Niên khóa: {currentYearName || '2026 - 2027'}</p>
        </section>

        {/* PHẦN THÔNG TIN PHÒNG HỌC & GLV */}
        <section className="ptl-meta-section">
          <div className="ptl-meta-item">
            <span className="ptl-meta-label">PHÒNG:</span>
            <span className="ptl-meta-value">{roomName}</span>
          </div>

          <div className="ptl-meta-item ptl-meta-teachers">
            <span className="ptl-meta-label">ĐỨNG LỚP:</span>
            <div className="ptl-teachers-list">
              {assignedTeachers.length > 0 ? (
                assignedTeachers.map((teacher, index) => (
                  <div key={index} className="ptl-teacher-line">
                    • {teacher}
                  </div>
                ))
              ) : (
                <div className="ptl-teacher-line">— Chưa có phân công</div>
              )}
            </div>
          </div>
        </section>

        {/* BẢNG DANH SÁCH ĐÓNG HỌC PHÍ (6 CỘT) */}
        <section className="ptl-table-section">
          <table className="ptl-table">
            <thead>
              <tr>
                <th className="ptl-col-stt">STT</th>
                <th className="ptl-col-id">Mã ID</th>
                <th className="ptl-col-holy">Tên Thánh</th>
                <th className="ptl-col-name">Họ và Tên</th>
                <th className="ptl-col-date">Ngày đóng tiền</th>
                <th className="ptl-col-sign">Ký tên</th>
              </tr>
            </thead>
            <tbody>
              {sortedStudents.length > 0 ? (
                sortedStudents.map((student, idx) => {
                  const record = tuitionMap[student.id];
                  // Nếu đã thu hoặc đã xác nhận thì hiển thị ngày, ngược lại để trống
                  const isPaid = record && (record.status === 'collected' || record.status === 'confirmed');
                  const paidDateDisplay = isPaid && record.collectedDate ? formatDisplayDate(record.collectedDate) : '';

                  return (
                    <tr key={student.id || idx}>
                      <td className="ptl-text-center">{idx + 1}</td>
                      <td className="ptl-col-id ptl-text-center">{student.id}</td>
                      <td>{student.holyName || '-'}</td>
                      <td className="ptl-font-bold">{student.fullName}</td>
                      <td className="ptl-text-center ptl-col-date-val">{paidDateDisplay}</td>
                      <td className="ptl-col-sign-val"></td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="ptl-text-center" style={{ padding: '24px', color: '#64748b' }}>
                    Chưa có thiếu nhi nào trong lớp này.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
};

export default PrintTuitionList;